// src/views/examples/AdminEmployee.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardHeader,
  CardFooter,
  Table,
  Container,
  Row,
  Col,
  Button,
  Input,
  CardBody,
  Pagination,
  PaginationItem,
  PaginationLink,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
} from "reactstrap";

import Header from "components/Headers/Header";
import { useTablePaging, PAGE_SIZE_OPTIONS } from "../stores/pagingStore";
import Swal from "sweetalert2";
import LoadingSpinner from "components/LoadingSpinner";
import axios from "axios";

const TABLE_KEY = "employees";

// ✅ BASE จาก env
const API_BASE = (process.env.REACT_APP_API_BASE || "").replace(/\/+$/, "");
const API_USERS = `${API_BASE}/users`; // GET list, POST create
const apiUserUrl = (username) =>
  `${API_USERS}/${encodeURIComponent(String(username || "").trim())}`;

// ✅ ดึง users
async function fetchUsers() {
  const res = await axios.get(API_USERS, {
    headers: { "Content-Type": "application/json" },
  });

  const raw = res?.data;

  // ✅ ตรงกับรูป: { data: [...] }
  const rows = Array.isArray(raw?.data)
    ? raw.data
    : Array.isArray(raw)
      ? raw
      : Array.isArray(raw?.data?.rows)
        ? raw.data.rows
        : [];

  return { rows, total: rows.length };
}

const User = () => {
  const { page, pageSize, setPage, setPageSize } = useTablePaging(
    TABLE_KEY,
    10,
  );

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [keyword, setKeyword] = useState("");

  // ✅ Add User Modal state
  const [showAdd, setShowAdd] = useState(false);
  const [newUsername, setNewUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [saving, setSaving] = useState(false);

  // ✅ responsive
  const [isSmall, setIsSmall] = useState(window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsSmall(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const pageIndex = Math.max(0, Number(page || 1) - 1);

  const load = async () => {
    if (!API_BASE) {
      setError("ไม่ได้ตั้งค่า REACT_APP_API_BASE ในไฟล์ .env");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const { rows } = await fetchUsers();
      setRows(rows);
    } catch (err) {
      setRows([]);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "ไม่สามารถโหลดข้อมูลผู้ใช้ได้",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!alive) return;
      await load();
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ field สำหรับแสดงผล (รองรับหลายชื่อ)
  const renderUsername = (r) =>
    r?.username || r?.userName || r?.empUsername || "-";

  // ✅ format date ให้ดูง่าย (ไทย) — ถ้า value ไม่มีคืน "-"
  const fmt = (iso) => {
    if (!iso) return "-";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleString("th-TH", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // ✅ filter: โชว์เฉพาะ isActive true + ค้นหา username
  const displayRowsFiltered = useMemo(() => {
    const k = keyword.trim().toLowerCase();

    return rows
      .filter((r) => r?.isActive === true) // ✅ เอาเฉพาะ active
      .filter((r) => {
        if (!k) return true;
        return String(renderUsername(r)).toLowerCase().includes(k);
      });
  }, [rows, keyword]);

  // ✅ client paging
  const displayRows = useMemo(() => {
    const start = pageIndex * pageSize;
    const end = start + pageSize;
    return displayRowsFiltered.slice(start, end);
  }, [displayRowsFiltered, pageIndex, pageSize]);

  const filteredTotal = displayRowsFiltered.length;
  const pageCount = Math.max(1, Math.ceil(filteredTotal / (pageSize || 10)));
  const current = Math.min(Number(page || 1), pageCount);

  const gotoPage = (p) => {
    const next = Math.min(Math.max(1, p), pageCount);
    setPage(TABLE_KEY, next);
  };

  const pageItems = useMemo(() => {
    const items = [];
    const maxAround = 2;
    const start = Math.max(1, current - maxAround);
    const end = Math.min(pageCount, current + maxAround);
    for (let p = start; p <= end; p++) items.push(p);
    return items;
  }, [current, pageCount]);

  const openAddModal = () => {
    setNewUsername("");
    setNewPassword("");
    setShowAdd(true);
  };

  const closeAddModal = () => {
    if (saving) return;
    setShowAdd(false);
  };

  const handleCreateUser = async () => {
    const u = newUsername.trim();
    const p = newPassword.trim();

    if (!u || !p) {
      await Swal.fire({
        icon: "warning",
        title: "Incomplete information",
        text: "Please enter your Username and Password.",
      });
      return;
    }

    try {
      setSaving(true);

      await axios.post(
        API_USERS,
        { username: u, password: p },
        { headers: { "Content-Type": "application/json" } },
      );

      await Swal.fire({
        icon: "success",
        title: "Success",
        text: "User created successfully.",
        timer: 1200,
        showConfirmButton: false,
      });

      setShowAdd(false);
      await load();
      gotoPage(1);
    } catch (e) {
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: e?.response?.data?.message || e?.message || "Create user failed",
      });
    } finally {
      setSaving(false);
    }
  };

  // ✅ Suspend: DELETE /users/${username}
  const handleSuspend = async (row) => {
    const username = renderUsername(row);
    if (!username || username === "-") return;

    const confirm = await Swal.fire({
      icon: "warning",
      title: "Suspend user?",
      html: `Do you want to suspend user <b>${username}</b>?`,
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Suspend",
      cancelButtonText: "Cancel",
    });
    if (!confirm.isConfirmed) return;

    try {
      setLoading(true);

      await axios.delete(apiUserUrl(username), {
        headers: { "Content-Type": "application/json" },
      });

      await Swal.fire({
        icon: "success",
        title: "Suspended",
        text: `Suspend ${username} successful`,
        timer: 1200,
        showConfirmButton: false,
      });

      await load();

      const newTotal = Math.max(0, filteredTotal - 1);
      const newPageCount = Math.max(1, Math.ceil(newTotal / pageSize));
      if (current > newPageCount) gotoPage(newPageCount);
    } catch (e) {
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: e?.response?.data?.message || e?.message || "Suspend failed",
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <LoadingSpinner fullPage />;

  return (
    <>
      <Header />
      <Container className="mt--7" fluid>
        <Row>
          <Col xs="12">
            <Card className="bg-secondary shadow">
              <CardHeader className="border-0 d-flex align-items-center justify-content-between">
                <h6 className="mb-0">User</h6>
                <Button color="success" onClick={openAddModal}>
                  Add User
                </Button>
              </CardHeader>

              <CardBody className="position-relative">
                {/* Toolbar */}
                <div className="mb-3">
                  <Row className="align-items-center">
                    <Col
                      xs="12"
                      md="6"
                      className="d-flex align-items-center"
                      style={{ gap: 8 }}
                    >
                      <Input
                        type="select"
                        value={pageSize}
                        onChange={(e) => {
                          setPageSize(TABLE_KEY, Number(e.target.value));
                          gotoPage(1);
                        }}
                        style={{ width: 96 }}
                      >
                        {PAGE_SIZE_OPTIONS.map((n) => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </Input>
                      <span className="text-muted">Row/Page</span>
                    </Col>

                    <Col
                      xs="12"
                      md="6"
                      className="mt-2 mt-md-0 d-flex justify-content-md-end"
                    >
                      <Input
                        id="userSearch"
                        placeholder="username"
                        value={keyword}
                        onChange={(e) => {
                          setKeyword(e.target.value);
                          gotoPage(1);
                        }}
                        style={{ maxWidth: 320, minWidth: 260 }}
                      />
                    </Col>
                  </Row>
                </div>

                {isSmall ? (
                  <div className="table-responsive">
                    <Table hover className="table table-modern mb-0 zi-mobile">
                      <thead className="thead-colored">
                        <tr>
                          <th className="text-start">#</th>
                          <th className="text-start">Username</th>
                          <th className="text-start">Created At</th>
                          <th className="text-start">Updated At</th>
                          <th className="text-end">Management</th>
                        </tr>
                      </thead>

                      <tbody>
                        {!!error && (
                          <tr>
                            <td
                              colSpan={5}
                              className="text-center text-danger py-4"
                            >
                              {error}
                            </td>
                          </tr>
                        )}

                        {!error && displayRows.length === 0 && (
                          <tr>
                            <td
                              colSpan={5}
                              className="text-center text-muted py-4"
                            >
                              ไม่พบข้อมูล
                            </td>
                          </tr>
                        )}

                        {!error &&
                          displayRows.map((r, idx) => (
                            <tr
                              key={
                                r?.id ||
                                r?.userId ||
                                `${idx}-${renderUsername(r)}`
                              }
                            >
                              <td data-th="#" className="text-start">
                                {pageIndex * pageSize + idx + 1}
                              </td>
                              <td data-th="Username" className="text-start">
                                {renderUsername(r)}
                              </td>
                              <td data-th="Created At" className="text-start">
                                {fmt(r?.createdAt)}
                              </td>
                              <td data-th="Updated At" className="text-start">
                                {fmt(r?.updatedAt)}
                              </td>
                              <td data-th="Management" className="text-end">
                                <div className="d-inline-flex gap-2">
                                  <Button
                                    color="danger"
                                    size="sm"
                                    onClick={() => handleSuspend(r)}
                                  >
                                    Suspend
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <div className="zi-table-wrap">
                    <Table responsive hover className="zi-table mb-0">
                      <thead>
                        <tr>
                          <th className="text-start" style={{ width: 100 }}>
                            #
                          </th>
                          <th className="text-start" style={{ width: 260 }}>
                            Username
                          </th>
                          <th className="text-start" style={{ width: 220 }}>
                            Created At
                          </th>
                          <th className="text-start" style={{ width: 220 }}>
                            Updated At
                          </th>
                          <th className="text-end" style={{ width: 160 }}>
                            Management
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {!!error ? (
                          <tr>
                            <td
                              colSpan={6}
                              className="text-center text-danger py-4"
                            >
                              {error}
                            </td>
                          </tr>
                        ) : displayRows.length === 0 ? (
                          <tr>
                            <td
                              colSpan={6}
                              className="text-center text-muted py-4"
                            >
                              ไม่พบข้อมูล
                            </td>
                          </tr>
                        ) : (
                          displayRows.map((r, idx) => (
                            <tr
                              key={
                                r?.id ||
                                r?.userId ||
                                `${idx}-${renderUsername(r)}`
                              }
                            >
                              <td className="text-start">
                                {pageIndex * pageSize + idx + 1}
                              </td>
                              <td className="text-start fw-600">
                                {renderUsername(r)}
                              </td>
                              <td className="text-start">
                                {fmt(r?.createdAt)}
                              </td>
                              <td className="text-start">
                                {fmt(r?.updatedAt)}
                              </td>
                              <td className="text-end">
                                <div className="d-inline-flex gap-2">
                                  <Button
                                    color="danger"
                                    size="sm"
                                    onClick={() => handleSuspend(r)}
                                  >
                                    Suspend
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                )}

                <div className="d-flex justify-content-end justify-content-sm-end justify-content-center mt-2">
                  <div className="text-muted small">
                    Showing{" "}
                    {(current - 1) * pageSize + (displayRows.length ? 1 : 0)}–
                    {Math.min(
                      current * pageSize,
                      (current - 1) * pageSize + displayRows.length,
                    )}{" "}
                    of {filteredTotal} entries
                  </div>
                </div>

                <style>{`
                  .zi-table-wrap{
                    background:#fff;
                    border-radius:10px;
                    border:1px solid rgba(0,0,0,.06);
                    overflow:hidden;
                  }
                  .zi-table{ margin:0; }
                  .zi-table thead th{
                    background:#f7fafc;
                    color:#0f172a;
                    font-weight:700;
                    font-size:14px;
                    border-bottom:1px solid rgba(0,0,0,.06);
                    padding:14px 16px;
                    white-space:nowrap;
                  }
                  .zi-table tbody td{
                    padding:14px 16px;
                    border-top:1px solid rgba(0,0,0,.06);
                    vertical-align:middle;
                    font-size:14px;
                    color:#0f172a;
                  }
                  .zi-table tbody tr:hover{ background:#f9fbff; }
                  .fw-600{ font-weight:600; }
                  .gap-2{ gap:8px; }

                  /* ✅ MOBILE: show data-th label */
                  @media (max-width: 767.98px){
                    table.zi-mobile thead{ display:none; }
                    table.zi-mobile tbody tr{
                      display:block;
                      border:1px solid rgba(0,0,0,.08);
                      border-radius:10px;
                      background:#fff;
                      margin-bottom:10px;
                      overflow:hidden;
                    }
                    table.zi-mobile tbody td{
                      display:flex;
                      align-items:flex-start;
                      justify-content:space-between;
                      gap:12px;
                      padding:10px 12px;
                      border-top:1px solid rgba(0,0,0,.06);
                      white-space:normal;
                    }
                    table.zi-mobile tbody td::before{
                      content: attr(data-th);
                      font-weight:700;
                      color:#64748b;
                      flex:0 0 42%;
                    }
                    table.zi-mobile tbody td:last-child{
                      border-bottom:none;
                    }
                  }
                `}</style>

                {/* Add User Modal */}
                <Modal isOpen={showAdd} toggle={closeAddModal} centered>
                  <ModalHeader toggle={closeAddModal}>Add User</ModalHeader>

                  <ModalBody>
                    <Form
                      onSubmit={(e) => {
                        e.preventDefault();
                        handleCreateUser();
                      }}
                    >
                      <FormGroup>
                        <Label className="form-control-label mb-1">
                          Username
                        </Label>
                        <Input
                          value={newUsername}
                          onChange={(e) => setNewUsername(e.target.value)}
                          placeholder="Username"
                          autoComplete="off"
                        />
                      </FormGroup>

                      <FormGroup className="mb-0">
                        <Label className="form-control-label mb-1">
                          Password
                        </Label>
                        <Input
                          type="password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          placeholder="Password"
                          autoComplete="new-password"
                        />
                      </FormGroup>
                    </Form>
                  </ModalBody>

                  <ModalFooter>
                    <Button
                      color="secondary"
                      onClick={closeAddModal}
                      disabled={saving}
                    >
                      Back
                    </Button>
                    <Button
                      color="success"
                      onClick={handleCreateUser}
                      disabled={saving}
                    >
                      {saving ? "Submitting..." : "Submit"}
                    </Button>
                  </ModalFooter>
                </Modal>
              </CardBody>

              <CardFooter className="py-4">
                <nav aria-label="users pagination">
                  <Pagination className="pagination justify-content-center mb-0">
                    <PaginationItem disabled={current <= 1}>
                      <PaginationLink onClick={() => gotoPage(current - 1)}>
                        <i className="fas fa-angle-left" />
                      </PaginationLink>
                    </PaginationItem>

                    {pageItems.map((p) => (
                      <PaginationItem key={p} active={p === current}>
                        <PaginationLink onClick={() => gotoPage(p)}>
                          {p}
                        </PaginationLink>
                      </PaginationItem>
                    ))}

                    <PaginationItem disabled={current >= pageCount}>
                      <PaginationLink onClick={() => gotoPage(current + 1)}>
                        <i className="fas fa-angle-right" />
                      </PaginationLink>
                    </PaginationItem>
                  </Pagination>
                </nav>
              </CardFooter>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default User;
